import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import LinkExtension from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Bold, Italic, Strikethrough, List, Quote, Table as TableIcon, Code, Link as LinkIcon } from 'lucide-react';

import UserInfo from '../../../interfaces/UserInfo';
import Tooltip from '../../ui/Tooltip/Tooltip';
import getMentionSuggestions from './utils/MentionSuggestion';
import getTaskSuggestions from './utils/TaskSuggestion';
import { EmojiSuggestion } from './utils/EmojiSuggestion';
import { useParticipantTaskReferenceSearch } from '../../../hooks/useParticipantTaskReferenceSearch';
import { useResourceReferenceSearch } from '../../../hooks/useResourceReferenceSearch';
import { ResourceSuggestion } from './utils/ResourceSuggestion';
import './MessageItem.css';
import InsertResourceLink from '../resourceChat/InsertResourceLink';
import { useAuth } from '../../../firebase/AuthProvider';
import { resourceReferenceId, resourceReferenceContent } from '../../../services/resources/ResourceChatService';

export interface MessageEditorRef {
  insertEmoji: (emoji: string) => void;
  clearContent: () => void;
  setContent: (content: any, options?: { focus?: boolean; emitUpdate?: boolean }) => void;
}

interface MessageEditorProps {
  onUpdate: (plaintext: string, jsonDoc: any, isEmpty: boolean) => void;
  onSendMessage: () => void;
  onPasteFile?: (file: File) => boolean | void;
  users?: UserInfo[];
  compact?: boolean;
  showFormatting?: boolean;
  canSendWithoutText?: boolean;
  appearance?: "default" | "meeting-dark";
  placeholder?: string;
}

const MenuBar = ({ editor, floating = false, isDark = false }: { editor: Editor | null; floating?: boolean; isDark?: boolean }) => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const linkModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (linkModalRef.current && !linkModalRef.current.contains(event.target as Node)) {
        setIsLinkModalOpen(false);
      }
    };
    if (isLinkModalOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLinkModalOpen]);

  if (!editor) return null;

  const btnClass = (isActive: boolean) => 
    `chat-format-button ${isActive ? 'chat-format-button-active' : ''} p-1.5 rounded-lg transition-colors flex items-center justify-center outline-none ${
      isActive 
        ? isDark ? 'bg-[#7E57C2]/25 text-purple-200 shadow-sm' : 'bg-indigo-100 text-indigo-700 shadow-sm'
        : isDark ? 'text-slate-400 hover:bg-slate-700 hover:text-slate-100' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-900'
    }`;

  const TooltipText = ({ text }: { text: string }) => <span className="text-xs font-semibold">{text}</span>;

  const openLinkModal = () => {
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, ' ');
    const href = editor.getAttributes('link').href || '';

    setLinkText(text);
    setLinkUrl(href);
    setIsLinkModalOpen(true);
  };

  const applyLink = () => {
    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setIsLinkModalOpen(false);
      return;
    }

    let formattedUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl) && !formattedUrl.startsWith('mailto:') && !formattedUrl.startsWith('tel:')) {
        formattedUrl = `https://${formattedUrl}`;
    }

    const finalDisplayText = linkText.trim() || formattedUrl;

    editor.chain().focus()
        .extendMarkRange('link')
        .insertContent({
            type: 'text',
            text: finalDisplayText,
            marks: [{ type: 'link', attrs: { href: formattedUrl } }]
        })
        .run();

    setIsLinkModalOpen(false);
  };

  return (
    <div
      className={`chat-format-toolbar flex h-fit shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 py-2 ${isDark ? 'bg-slate-800' : 'bg-white dark:bg-[#1d202b]'} ${
        floating
          ? `absolute bottom-[calc(100%+48px)] left-0 z-[80] max-w-[calc(100vw-48px)] flex-nowrap overflow-x-auto rounded-xl border-[1px] shadow-[0_10px_30px_rgba(15,23,42,0.16)] ${isDark ? 'border-slate-700' : 'border-slate-200'}`
          : `relative flex-wrap rounded-t-2xl border-b-[1px] ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-slate-50/50'}`
      }`}
    >
      <Tooltip content={<TooltipText text="Bold" />} position="top">
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))}><Bold size={15} /></button>
      </Tooltip>
      <Tooltip content={<TooltipText text="Italic" />} position="top">
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))}><Italic size={15} /></button>
      </Tooltip>
      <Tooltip content={<TooltipText text="Strikethrough" />} position="top">
        <button onClick={() => editor.chain().focus().toggleStrike().run()} className={btnClass(editor.isActive('strike'))}><Strikethrough size={15} /></button>
      </Tooltip>
      
      <div className={`w-px h-5 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
      
      <Tooltip content={<TooltipText text="Bullet List" />} position="top">
        <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))}><List size={15} /></button>
      </Tooltip>
      <Tooltip content={<TooltipText text="Blockquote" />} position="top">
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnClass(editor.isActive('blockquote'))}><Quote size={15} /></button>
      </Tooltip>
      
      <div className={`w-px h-5 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

      <Tooltip content={<TooltipText text="Code Block" />} position="top">
        <button onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={btnClass(editor.isActive('codeBlock'))}><Code size={15} /></button>
      </Tooltip>
      
      <div className="relative">
        <Tooltip content={<TooltipText text="Link" />} position="top">
            <button onClick={openLinkModal} className={btnClass(editor.isActive('link') || isLinkModalOpen)}><LinkIcon size={15} /></button>
        </Tooltip>

        {/* FIX: Changed left-0 to right-0 and narrowed width to w-64 so it expands inwards securely */}
        {isLinkModalOpen && (
            <div ref={linkModalRef} className={`chat-link-modal absolute bottom-full right-0 mb-2 w-64 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-3 z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200 ${isDark ? 'bg-slate-800' : 'bg-white dark:bg-[#1d202b]'}`}>
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                        <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>Text to display</label>
                        <input
                            type="text"
                            value={linkText}
                            onChange={(e) => setLinkText(e.target.value)}
                            placeholder="Optional"
                            className={`w-full px-2.5 py-1.5 text-[13px] border-[1px] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:border-[#7E57C2] transition-all ${isDark ? 'text-slate-100 bg-slate-900 border-slate-700 placeholder:text-slate-500' : 'text-slate-800 bg-slate-50 border-slate-200 focus:bg-white'}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>Link URL</label>
                        <input
                            type="text"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            placeholder="https://example.com"
                            className={`w-full px-2.5 py-1.5 text-[13px] border-[1px] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:border-[#7E57C2] transition-all ${isDark ? 'text-slate-100 bg-slate-900 border-slate-700 placeholder:text-slate-500' : 'text-slate-800 bg-slate-50 border-slate-200 focus:bg-white'}`}
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && applyLink()}
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-1">
                        <button onClick={() => setIsLinkModalOpen(false)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>Cancel</button>
                        <button onClick={applyLink} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#7E57C2] hover:bg-[#6F49B5] rounded-lg transition-colors shadow-sm">Save</button>
                    </div>
                </div>
            </div>
        )}
      </div>

      <div className={`w-px h-5 mx-1 ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />

      <InsertResourceLink editor={editor} />
      <Tooltip content={<TooltipText text="Insert Table" />} position="top">
        <button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} className={btnClass(editor.isActive('table'))}><TableIcon size={15} /></button>
      </Tooltip>
    </div>
  );
};

const getEditorAttributes = (isMeetingDark: boolean, describedBy: string, placeholder: string) => ({
  class: `chat-compose-prosemirror focus:outline-none w-full !h-auto text-[13.5px] ${
    isMeetingDark ? 'meeting-chat-prosemirror-dark text-slate-100' : 'text-slate-700'
  }
         [&_blockquote]:!border-l-4 [&_blockquote]:!border-indigo-400 [&_blockquote]:!pl-3 [&_blockquote]:pr-2 [&_blockquote]:py-1 [&_blockquote]:my-2 [&_blockquote]:italic [&_blockquote]:rounded-r-lg
         [&_pre]:bg-slate-800 [&_pre]:text-slate-50 [&_pre]:p-3 [&_pre]:rounded-xl [&_pre]:my-2 [&_pre]:font-mono [&_pre]:text-xs [&_pre]:overflow-x-auto
         [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_table]:text-sm [&_table]:rounded-lg [&_table]:overflow-hidden`.replace(/\s+/g, ' '),
  style: isMeetingDark ? 'color:#f1f5f9;caret-color:#f8fafc;' : '',
  role: 'textbox',
  'aria-label': 'Message editor',
  'aria-multiline': 'true',
  'aria-placeholder': placeholder,
  'aria-describedby': describedBy,
});

const MessageEditor = forwardRef<MessageEditorRef, MessageEditorProps>(
  ({ onUpdate, onSendMessage, onPasteFile, users = [], compact = true, showFormatting = false, canSendWithoutText = false, appearance = "default", placeholder }, ref) => {
    const { currentUser } = useAuth();
    const [resourcePasteError, setResourcePasteError] = useState('');
    const isMeetingDark = appearance === "meeting-dark";
    const shortcutDescriptionId = useRef(`chat-shortcuts-${Math.random().toString(36).slice(2, 9)}`).current;
    const placeholderText = placeholder || (compact ? 'Message…' : 'Type a message…');
    const [isFocused, setIsFocused] = useState(false);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);
    const [showShortcutHint, setShowShortcutHint] = useState(false);
    const shortcutHintTimerRef = useRef<number>();
    const usersRef = useRef(users);
    useEffect(() => { usersRef.current = users; }, [users]);
    const searchParticipantTasks = useParticipantTaskReferenceSearch();
    const searchParticipantTasksRef = useRef(searchParticipantTasks);
    useEffect(() => { searchParticipantTasksRef.current = searchParticipantTasks; }, [searchParticipantTasks]);
    const resourceReferences = useResourceReferenceSearch();
    const resourceReferencesRef = useRef(resourceReferences);
    resourceReferencesRef.current = resourceReferences;

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ link: false }),
        Placeholder.configure({
          placeholder: placeholderText,
        }),
        Mention.configure({
          HTMLAttributes: { class: 'chat-mention bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-md font-semibold inline-block' },
          suggestions: [
            getMentionSuggestions(() => usersRef.current),
            getTaskSuggestions(query => searchParticipantTasksRef.current(query)),
          ],
          renderText({ node }) {
            return node.attrs.mentionSuggestionChar === '#'
              ? `#${node.attrs.label ?? node.attrs.id}`
              : `@${node.attrs.label ?? node.attrs.id}`;
          },
          renderHTML({ node, options }) {
            if (node.attrs.mentionSuggestionChar === '#') {
              return ['a', {
                href: `/task/${encodeURIComponent(String(node.attrs.id))}`,
                class: 'chat-task-reference',
                'data-task-reference': node.attrs.id,
              }, `#${node.attrs.label ?? node.attrs.id}`];
            }
            return ['span', options.HTMLAttributes, `@${node.attrs.label ?? node.attrs.id}`];
          },
        }),
        EmojiSuggestion,
        ResourceSuggestion.configure({
          search: query => resourceReferencesRef.current.search(query),
          preview: id => resourceReferencesRef.current.preview(id),
          onError: () => setResourcePasteError('This resource is no longer available.'),
        }),
        LinkExtension.configure({
          openOnClick: false,
          HTMLAttributes: { class: 'chat-message-link text-indigo-600 underline cursor-pointer hover:text-indigo-800 transition-colors font-medium break-all' },
        }),
        Table.configure({ resizable: true }),
        TableRow, TableHeader, TableCell,
      ],
      editorProps: {
        attributes: getEditorAttributes(isMeetingDark, shortcutDescriptionId, placeholderText),
        handlePaste: (_view, event) => {
          const resourceId = resourceReferenceId(event.clipboardData?.getData('text/plain')?.trim() ?? '');
          if (resourceId && currentUser) {
            event.preventDefault(); setResourcePasteError('');
            const originalDoc = editor?.state.doc;
            const originalRange = editor ? { from: editor.state.selection.from, to: editor.state.selection.to } : null;
            resourceReferencesRef.current.preview(resourceId)
              .then(() => { if (editor && !editor.isDestroyed && originalRange && originalDoc && editor.state.doc.eq(originalDoc)) editor.chain().focus().insertContentAt(originalRange, [resourceReferenceContent(resourceId), { type: 'text', text: ' ' }]).run(); })
              .catch(() => setResourcePasteError('Resource unavailable.'));
            return true;
          }
          if (!onPasteFile || !event.clipboardData) return false;

          const imageItem = Array.from(event.clipboardData.items).find(
            (item) => item.kind === 'file' && item.type.startsWith('image/')
          );

          if (!imageItem) return false;

          const clipboardFile = imageItem.getAsFile();
          if (!clipboardFile) return false;

          const extension = clipboardFile.type.split('/')[1] || 'png';
          const file = new File(
            [clipboardFile],
            clipboardFile.name && clipboardFile.name !== 'image.png'
              ? clipboardFile.name
              : `clipboard-image-${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`,
            { type: clipboardFile.type, lastModified: Date.now() }
          );

          const accepted = onPasteFile(file);
          if (accepted === false) return false;

          event.preventDefault();
          return true;
        },
        handleKeyDown: (view, event) => {
          if (document.querySelector('.tippy-box[data-state="visible"]')) return false;

          if (event.key === 'Enter' && event.shiftKey) {
            event.preventDefault();
            editor?.commands.splitBlock(); 
            return true;
          }

          if (event.key === 'Enter' && !event.shiftKey) {
            const isFormattingActive = editor?.isActive('bulletList') || 
                                       editor?.isActive('orderedList') || 
                                       editor?.isActive('blockquote') ||
                                       editor?.isActive('codeBlock') ||
                                       editor?.isActive('table');
            
            if (isFormattingActive) return false;

            event.preventDefault();
            if (!editor?.isEmpty || canSendWithoutText) onSendMessage();
            return true;
          }
          
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        setIsEditorEmpty(editor.isEmpty);
        onUpdate(editor.getText(), editor.getJSON(), editor.isEmpty);
      },
      onFocus: () => {
        setIsFocused(true);
        setShowShortcutHint(true);
        window.clearTimeout(shortcutHintTimerRef.current);
        shortcutHintTimerRef.current = window.setTimeout(() => setShowShortcutHint(false), 4500);
      },
      onBlur: () => {
        setIsFocused(false);
        setShowShortcutHint(false);
        window.clearTimeout(shortcutHintTimerRef.current);
      },
    });

    useEffect(() => () => window.clearTimeout(shortcutHintTimerRef.current), []);

    useEffect(() => {
      if (!editor) return;
      editor.setOptions({
        editorProps: {
          ...editor.options.editorProps,
          attributes: getEditorAttributes(isMeetingDark, shortcutDescriptionId, placeholderText),
        },
      });
    }, [editor, isMeetingDark, placeholderText, shortcutDescriptionId]);

    useEffect(() => {
      if (!editor) return;
      const firstParagraph = editor.view.dom.querySelector<HTMLElement>('p:first-child');
      if (!firstParagraph) return;
      firstParagraph.classList.toggle('chat-compose-empty-paragraph', isEditorEmpty);
      if (isEditorEmpty) firstParagraph.setAttribute('data-placeholder', placeholderText);
      else firstParagraph.removeAttribute('data-placeholder');
    }, [editor, isEditorEmpty, placeholderText]);

    useImperativeHandle(ref, () => ({
      insertEmoji: (emoji: string) => editor?.chain().focus().insertContent(emoji).run(),
      clearContent: () => editor?.commands.clearContent(true),
      setContent: (content: any, options?: { focus?: boolean; emitUpdate?: boolean }) => {
          if (editor) {
              editor.commands.setContent(content, { emitUpdate: options?.emitUpdate ?? true });
              setIsEditorEmpty(editor.isEmpty);
              if (options?.focus !== false) editor.commands.focus('end');
          }
      }
    }));

    if (!editor) return null;

    return (
      <>
        <style>{`
          .meeting-chat-prosemirror-dark,
          .meeting-chat-prosemirror-dark p,
          .meeting-chat-prosemirror-dark li {
            color: #f1f5f9 !important;
          }
          .meeting-chat-prosemirror-dark p.is-editor-empty:first-child::before {
            color: #64748b !important;
          }
        `}</style>
      {resourcePasteError && <p role="alert" className="text-danger">{resourcePasteError}</p>}
      <div className={`chat-message-editor relative z-10 flex h-auto w-full flex-col ${isMeetingDark ? 'bg-slate-900' : 'bg-transparent'}`}>
        <span id={shortcutDescriptionId} className="sr-only">Use @ to mention people, # to reference participant tasks, and / to find resources.</span>
        {compact && isFocused && isEditorEmpty && showShortcutHint && (
          <div className={`chat-reference-shortcut-hint ${isMeetingDark ? 'chat-reference-shortcut-hint--dark' : ''} pointer-events-none absolute bottom-[calc(100%+7px)] left-1 z-[90] flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] shadow-lg`} aria-hidden="true">
            <span className="chat-reference-shortcut-hint__label font-semibold">Shortcuts</span>
            <span className="chat-reference-shortcut-hint__chip rounded px-1.5 py-0.5 font-mono font-semibold">@ People</span>
            <span className="chat-reference-shortcut-hint__chip rounded px-1.5 py-0.5 font-mono font-semibold"># Tasks</span>
            <span className="chat-reference-shortcut-hint__chip rounded px-1.5 py-0.5 font-mono font-semibold">/ Resources</span>
          </div>
        )}
        {(!compact || showFormatting) && <MenuBar editor={editor} floating={compact} isDark={isMeetingDark} />}

        <div className={`flex items-center ${compact ? "px-1 py-0" : "gap-1 px-2 py-1.5"}`}>
          <div
            className={`tiptap-editor relative flex min-w-0 flex-1 cursor-text flex-col rounded-lg transition-colors ${
              isMeetingDark ? "bg-slate-800" : "bg-transparent"
            }`}
            onClick={() => editor.chain().focus().run()}
          >
            <EditorContent
              editor={editor}
              className={`max-w-full shrink-0 ${
                isMeetingDark
                  ? "[&>.ProseMirror]:!text-slate-100 [&>.ProseMirror]:!caret-slate-100 [&>.ProseMirror_p]:!text-slate-100 [&>.ProseMirror_li]:!text-slate-100"
                  : ""
              } ${
                compact
                  ? "[&>.ProseMirror]:!min-h-[38px] [&>.ProseMirror]:!max-h-[92px] [&>.ProseMirror]:overflow-y-auto [&>.ProseMirror]:py-2 [&>.ProseMirror]:px-2 [&>.ProseMirror]:leading-5"
                  : "[&>.ProseMirror]:!h-auto [&>.ProseMirror]:!max-h-[160px] [&>.ProseMirror]:overflow-y-auto"
              }`}
            />
          </div>
        </div>
      </div>
      </>
    );
  }
);

MessageEditor.displayName = 'MessageEditor';
export default MessageEditor;
